import { Checkmark } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HoverView } from 'component/basics';
import { AnimatedYouTag } from 'component/tag/animated-you-tag';
import { UtilityText } from 'component/text';
import { useState } from 'react';
import { View } from 'react-native';
import { type TLabel } from 'util/stdtypes';

export type VoteRadioInputProps = {
    label?: TLabel;
    text?: string;
    selected?: boolean;
    submitted?: boolean;
    percent?: number;
    onPress?: () => void;
    stopPropagation?: boolean;
    testID?: string;
};

export function VoteRadioInput({
    label,
    text,
    selected = false,
    submitted = false,
    percent,
    onPress,
    stopPropagation,
    testID,
}: VoteRadioInputProps) {
    const { colors } = useTheme();
    const s = useVoteRadioInputStyles();
    const showResults = percent !== undefined;
    const borderColor = submitted ? colors.colorBorderAccent : selected ? colors.colorBorderSelected : colors.colorBorderPrimary;
    const [isHovering, setIsHovering] = useState(false);

    return (
        <HoverView
            hoverStyle={s.hover}
            onPress={onPress}
            setHover={setIsHovering}
            stopPropagation={stopPropagation}
            style={[s.button, { borderColor }]}
            testID={testID}
        >
            {showResults && (
                <View
                    style={[
                        s.fillBar,
                        {
                            width: `${percent}%`,
                            backgroundColor: submitted ? colors.colorDataVizScale3 : colors.colorDataVizOther,
                        },
                    ]}
                />
            )}
            <UtilityText color={colors.colorTextPrimary} label={label} text={text} type='small' />
            <View style={s.rightSide}>
                {showResults ? (
                    <View style={s.resultsRight}>
                        {submitted && <AnimatedYouTag />}
                        <UtilityText color={colors.colorTextPrimary} text={`${percent}%`} type='tiny' weight='strong' />
                    </View>
                ) : (
                    <View style={[s.circle, selected && s.circleSelected, isHovering && !selected && s.circleHover]}>
                        {selected && <Checkmark color={colors.colorTextOnPrimaryButton} size={16} />}
                    </View>
                )}
            </View>
        </HoverView>
    );
}

const useVoteRadioInputStyles = createThemedStyles((tokens) => ({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: 40,
        paddingHorizontal: 12,
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderRadius: 32,
        borderWidth: 1,
        overflow: 'hidden',
    },
    hover: {
        backgroundColor: 'rgba(0,0,0,0.1)',
    },
    fillBar: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        borderRadius: 32,
    },
    rightSide: {
        flexShrink: 0,
    },
    resultsRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    circle: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: tokens.colors.colorBorderPrimary,
        backgroundColor: tokens.colors.colorSurfacePrimary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    circleSelected: {
        backgroundColor: tokens.colors.colorButtonPrimary,
        borderColor: tokens.colors.colorButtonPrimary,
    },
    circleHover: {
        backgroundColor: 'transparent',
    },
}));
